import { signal } from '@preact/signals';
import { runAgent } from '@/agent/client';
import { applyActions, validateAction } from '@/agent/proposals';
import type { ChatMessage, Proposal } from '@/agent/types';
import { db } from '@/lib/db';
import { enqueue } from '@/lib/outbox';
import { showToast } from '@/state/ui';
import { undoFeeding } from '@/lib/mutations';

export const chatMessages = signal<ChatMessage[]>([]);
export const agentBusy = signal(false);
export const agentStatus = signal<string | null>(null); // "checking the roster…"
export const chatLoaded = signal(false);

/** Hook point for Phase 5 TTS — replaced by tts.ts when voice lands. */
export let speak: (text: string) => void = () => {};
export function setSpeaker(fn: (text: string) => void) {
  speak = fn;
}

const todayKey = () => new Date().toISOString().slice(0, 10);

export async function loadChat() {
  const rows = await db.messages.where('day').equals(todayKey()).sortBy('created_at');
  chatMessages.value = rows.map((r) => {
    const c = r.content as Partial<ChatMessage>;
    return {
      id: r.id,
      role: r.role as 'user' | 'assistant',
      text: c.text ?? '',
      voice: c.voice,
      proposal: c.proposal,
      proposalStatus: c.proposalStatus === 'pending' ? 'dismissed' : c.proposalStatus,
      appliedLabels: c.appliedLabels,
      reportId: c.reportId,
      createdAt: new Date(r.created_at).getTime(),
    };
  });
  chatLoaded.value = true;
}

async function persist(m: ChatMessage) {
  const row = {
    id: m.id,
    day: todayKey(),
    role: m.role,
    content: {
      text: m.text,
      voice: m.voice,
      proposal: m.proposal,
      proposalStatus: m.proposalStatus,
      appliedLabels: m.appliedLabels,
      reportId: m.reportId,
    },
    created_at: new Date(m.createdAt).toISOString(),
  };
  await db.messages.put(row);
  await enqueue({ table: 'messages', op: 'upsert', values: row as never });
}

function update(id: string, patch: Partial<ChatMessage>) {
  chatMessages.value = chatMessages.value.map((m) => (m.id === id ? { ...m, ...patch } : m));
}

/** Conversation history for the agent: proposals become compact text notes. */
function historyForAgent(): { role: 'user' | 'assistant'; content: string }[] {
  return chatMessages.value
    .filter((m) => m.text || m.proposal)
    .map((m) => {
      let content = m.text;
      if (m.proposal) {
        const status =
          m.proposalStatus === 'applied'
            ? `user CONFIRMED: ${(m.appliedLabels ?? []).join('; ') || 'all'}`
            : m.proposalStatus === 'dismissed'
              ? 'user did not confirm'
              : 'awaiting confirmation';
        content += `\n[proposed "${m.proposal.summary}" — ${m.proposal.actions.map((a) => a.label).join('; ')} → ${status}]`;
      }
      return { role: m.role, content: content.trim() };
    })
    .filter((m) => m.content.length > 0)
    .slice(-30);
}

export async function sendMessage(text: string, opts: { voice?: boolean } = {}) {
  const trimmed = text.trim();
  if (!trimmed || agentBusy.value) return;

  const userMsg: ChatMessage = {
    id: crypto.randomUUID(),
    role: 'user',
    text: trimmed,
    voice: opts.voice,
    createdAt: Date.now(),
  };
  chatMessages.value = [...chatMessages.value, userMsg];
  void persist(userMsg);

  const asstMsg: ChatMessage = {
    id: crypto.randomUUID(),
    role: 'assistant',
    text: '',
    streaming: true,
    createdAt: Date.now() + 1,
  };
  chatMessages.value = [...chatMessages.value, asstMsg];

  agentBusy.value = true;
  agentStatus.value = null;

  const TOOL_LABELS: Record<string, string> = {
    search_animals: 'checking the roster…',
    get_animal: 'pulling the chart…',
    get_due_board: 'reading the board…',
    get_schedules: 'reading schedules…',
    query_feed_logs: 'going through the log…',
    get_day_summary: 'tallying the day…',
  };

  try {
    let spokeStarted = false;
    for await (const ev of runAgent(historyForAgent())) {
      if (ev.type === 'token') {
        agentStatus.value = null;
        update(asstMsg.id, { text: (chatMessages.value.find((m) => m.id === asstMsg.id)?.text ?? '') + ev.text });
      } else if (ev.type === 'tool') {
        agentStatus.value = TOOL_LABELS[ev.name] ?? 'working…';
      } else if (ev.type === 'proposal') {
        const proposal = ev.proposal as Proposal;
        update(asstMsg.id, { proposal, proposalStatus: 'pending' });
        if (proposal.speak && !spokeStarted) {
          spokeStarted = true;
          speak(proposal.speak);
        }
      } else if (ev.type === 'done') {
        if (ev.text && !spokeStarted) speak(ev.text);
      } else if (ev.type === 'error') {
        const fallback =
          ev.message === 'offline'
            ? "I can't reach the conservancy server — the Now tab still works offline."
            : 'Something hiccupped on my end. The Now tab and roster still work — try me again in a minute.';
        update(asstMsg.id, {
          text: (chatMessages.value.find((m) => m.id === asstMsg.id)?.text ?? '') || fallback,
        });
      }
    }
  } finally {
    agentBusy.value = false;
    agentStatus.value = null;
    update(asstMsg.id, { streaming: false });
    const final = chatMessages.value.find((m) => m.id === asstMsg.id);
    if (final && (final.text || final.proposal)) void persist(final);
    else chatMessages.value = chatMessages.value.filter((m) => m.id !== asstMsg.id);
  }
}

const confirming = new Set<string>();

export async function confirmProposal(messageId: string, checkedIdx: number[]) {
  const msg = chatMessages.value.find((m) => m.id === messageId);
  if (!msg?.proposal || msg.proposalStatus !== 'pending') return;
  if (confirming.has(messageId)) return; // double-tap guard
  confirming.add(messageId);
  try {
    await doConfirm(msg, messageId, checkedIdx);
  } finally {
    confirming.delete(messageId);
  }
}

async function doConfirm(
  msg: ChatMessage,
  messageId: string,
  checkedIdx: number[]
) {
  if (!msg.proposal) return;
  const actions = msg.proposal.actions;
  const valid = checkedIdx.filter((i) => actions[i] && validateAction(actions[i]).ok);
  if (!valid.length) {
    const firstErr = checkedIdx.length ? validateAction(actions[checkedIdx[0]]).error : 'nothing selected';
    showToast({ kind: 'info', message: `Couldn't apply — ${firstErr}` });
    return;
  }
  try {
    const result = await applyActions(actions, valid);
    update(messageId, {
      proposalStatus: 'applied',
      appliedLabels: result.applied,
      reportId: result.reportId,
    });
    const updated = chatMessages.value.find((m) => m.id === messageId)!;
    void persist(updated);
    if (result.feedLogIds.length === 1 && result.applied.length === 1) {
      const logId = result.feedLogIds[0];
      showToast({
        kind: 'undo',
        message: result.applied[0],
        actionLabel: 'Undo',
        onAction: () => void undoFeeding(logId),
      }, 5000);
    } else if (result.applied.length) {
      showToast({ kind: 'success', message: `Done — ${result.applied.length} change${result.applied.length > 1 ? 's' : ''}` });
    }
  } catch (e) {
    showToast({ kind: 'info', message: `Couldn't apply: ${(e as Error).message}` });
  }
}

export function dismissProposal(messageId: string) {
  update(messageId, { proposalStatus: 'dismissed' });
  const updated = chatMessages.value.find((m) => m.id === messageId);
  if (updated) void persist(updated);
}
